/**
 * "Indique e ganhe": remembers the code of the link a person arrived through
 * (`?ref=CODE`) so the first Google sign-in can credit whoever shared it.
 *
 * The cookie is set on the parent domain (e.g. `.vidabit.app`), so a visit to
 * the brand site still counts when the person signs in on a space or on the
 * marketplace. It is read by this app only (the API receives it as `ref`).
 */

const COOKIE = 'vb_ref';
const MAX_AGE = 60 * 60 * 24 * 30; // 30 days
const CODE = /^[A-Za-z0-9]{4,16}$/;

function parentDomains(hostname: string): string[] {
  if (hostname === 'localhost' || hostname.endsWith('.localhost') || /^[\d.:]+$/.test(hostname)) {
    return [];
  }
  const labels = hostname.split('.');
  // Shortest first: the browser refuses public suffixes like "com.br".
  return [2, 3].filter((n) => labels.length >= n).map((n) => labels.slice(-n).join('.'));
}

export function readReferral(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)vb_ref=([^;]+)/);
  const value = match ? decodeURIComponent(match[1]) : null;
  return value && CODE.test(value) ? value : null;
}

/** Call once on page load. A newer link replaces an older one. */
export function rememberReferral(location: Location = window.location): void {
  const code = new URLSearchParams(location.search).get('ref')?.trim();
  if (!code || !CODE.test(code)) return;
  const base = `${COOKIE}=${encodeURIComponent(code.toUpperCase())}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax`;
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  for (const domain of parentDomains(location.hostname)) {
    document.cookie = `${base}; Domain=${domain}${secure}`;
    if (readReferral() === code.toUpperCase()) return;
  }
  document.cookie = `${base}${secure}`;
}
